import { forwardRef, Component, type ReactNode, type ErrorInfo } from 'react';
import {
  FormField as SharedFormField,
  type FormFieldProps as SharedFormFieldProps,
} from 'pi-kiosk-shared/ui';
import { captureCustomerBoundaryError } from '../observability/customerBoundarySentry.js';
import { getCustomerObservabilitySnapshot } from '../observability/CustomerObservabilityContext.js';

export type FormFieldProps = Omit<SharedFormFieldProps, 'surface'>;

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>((props, ref) => (
  <SharedFormField ref={ref} surface="customer" {...props} />
));
FormField.displayName = 'FormField';

class CustomerFormFieldErrorBoundary extends Component<
  { readonly children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { readonly children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureCustomerBoundaryError(error, errorInfo, getCustomerObservabilitySnapshot());
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

/** Marks auth/checkout subtrees that render customer-pinned FormField atoms (MFE-v3-B-04). */
export function CustomerFormFieldBoundary({ children }: { children: ReactNode }): JSX.Element {
  return (
    <CustomerFormFieldErrorBoundary>
      <div data-ui-form-field-surface="customer">{children}</div>
    </CustomerFormFieldErrorBoundary>
  );
}
